import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Database, Radar, FileCheck2, Cpu, ShieldCheck, CheckCircle2,
  Play, Pause, LayoutDashboard, MousePointer2, HardDriveUpload,
  Lock, Download,
} from "lucide-react";

/* lively per-module palette */
const C = {
  overview: "#6366F1", // indigo
  backup: "#0EA5E9",   // sky
  threat: "#F43F5E",   // rose
  compliance: "#10B981", // emerald
  ai: "#8B5CF6",       // violet
  green: "#16A34A",
};

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "backup", label: "Backup & Recovery", icon: Database },
  { key: "threat", label: "Threat Defense", icon: Radar },
  { key: "compliance", label: "Compliance Vault", icon: FileCheck2 },
  { key: "ai", label: "Vaultvec AI", icon: Cpu },
];

function Cursor({ delay = 1.0, to }) {
  return (
    <motion.div
      className="absolute z-30 pointer-events-none"
      initial={{ top: "85%", left: "6%", opacity: 0 }}
      animate={{ top: to.top, left: to.left, opacity: 1 }}
      transition={{ delay, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
    >
      <MousePointer2 className="h-5 w-5" style={{ color: "#0F172A" }} fill="#0F172A" />
      <motion.span
        className="absolute -top-2 -left-2 h-9 w-9 rounded-full border-2"
        style={{ borderColor: to.c }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: [0, 1.5], opacity: [0.9, 0] }}
        transition={{ delay: delay + 0.9, duration: 0.55 }}
      />
    </motion.div>
  );
}

const Pill = ({ color, children }) => (
  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-mono text-[10px] font-medium"
    style={{ background: `${color}1A`, color }}>
    <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} /> {children}
  </span>
);

const Btn = ({ color, filled, children }) => (
  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded font-medium text-[11px]"
    style={filled ? { background: color, color: "#fff" } : { color, border: `1px solid ${color}`, background: `${color}0D` }}>
    {children}
  </span>
);

/* ---------- scenes ---------- */
function Login() {
  return (
    <div className="h-full flex items-center justify-center p-6 relative">
      <Cursor delay={1.4} to={{ top: "64%", left: "50%", c: C.overview }} />
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-72 border border-slate-200 bg-white p-6 rounded-md shadow-sm">
        <div className="flex items-center gap-2 mb-5">
          <div className="h-6 w-6 flex items-center justify-center rounded" style={{ background: C.overview }}><ShieldCheck className="h-3.5 w-3.5 text-white" /></div>
          <span className="font-heading font-extrabold tracking-tighter text-slate-900">VAULTVEC</span>
        </div>
        <div className="space-y-2 font-mono text-[11px]">
          <div className="border border-slate-200 rounded px-3 py-2 text-slate-500 bg-slate-50">admin@vaultvec.io</div>
          <div className="border border-slate-200 rounded px-3 py-2 text-slate-500 bg-slate-50">•••••••••••</div>
        </div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.0 }} className="mt-3">
          <Btn color={C.overview} filled>Sign in <Lock className="h-3 w-3" /></Btn>
        </motion.div>
      </motion.div>
    </div>
  );
}

function Overview() {
  const stats = [["WORKLOADS", "6", C.backup], ["RESTORE PTS", "26", C.compliance], ["THREATS", "2", C.threat], ["GxP", "7/8", C.ai]];
  const bars = [60, 88, 74, 51, 62, 80, 92];
  return (
    <div className="p-6">
      <div className="grid grid-cols-4 gap-3 mb-4">
        {stats.map(([l, v, c], i) => (
          <motion.div key={l} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 * i }} className="rounded-md border border-slate-200 bg-white p-3">
            <div className="font-mono text-[9px] tracking-widest text-slate-400">{l}</div>
            <div className="font-mono text-xl mt-1 font-semibold" style={{ color: c }}>{v}</div>
          </motion.div>
        ))}
      </div>
      <div className="rounded-md border border-slate-200 bg-white p-4">
        <div className="font-mono text-[9px] tracking-widest text-slate-400 mb-3">7-DAY ACTIVITY</div>
        <div className="flex items-end gap-2 h-24">
          {bars.map((h, i) => (
            <motion.div key={i} initial={{ height: 0 }} animate={{ height: `${h}%` }} transition={{ delay: 0.2 + i * 0.07, duration: 0.5 }} className="flex-1 rounded-t" style={{ background: i % 2 ? C.backup : C.compliance }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Backup() {
  const rows = ["LIMS-PROD-01", "MES-BATCH-DB", "QC-LAB-STATION-07"];
  return (
    <div className="p-6 relative">
      <Cursor delay={1.2} to={{ top: "18%", left: "78%", c: C.backup }} />
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[10px] tracking-widest text-slate-400">PROTECTED WORKLOADS</span>
        <Btn color={C.backup}><HardDriveUpload className="h-3 w-3" /> Run backup</Btn>
      </div>
      {rows.map((r, i) => (
        <motion.div key={r} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 * i }} className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 mb-2 font-mono text-xs">
          <span className="flex items-center gap-2 text-slate-700"><Database className="h-3.5 w-3.5" style={{ color: C.backup }} /> {r}</span>
          <Pill color={C.green}>protected</Pill>
        </motion.div>
      ))}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2.4 }} className="rounded-md px-3 py-2 font-mono text-[11px] flex items-center gap-2 mt-1" style={{ background: `${C.green}14`, color: C.green, border: `1px solid ${C.green}33` }}>
        <CheckCircle2 className="h-3.5 w-3.5" /> New restore point sealed · sha256 verified
      </motion.div>
    </div>
  );
}

function Threat() {
  return (
    <div className="p-6 relative">
      <Cursor delay={1.6} to={{ top: "30%", left: "80%", c: C.threat }} />
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="rounded-md p-3 font-mono text-xs" style={{ background: `${C.threat}12`, border: `1px solid ${C.threat}40` }}>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 font-semibold" style={{ color: C.threat }}><Radar className="h-4 w-4" /> RANSOMWARE SIGNATURE</span>
          <Btn color={C.threat}>Resolve</Btn>
        </div>
        <div className="text-slate-500 mt-2">REGULATORY-FILE-SRV · LockBit variant detected</div>
      </motion.div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.6 }} className="rounded-md px-3 py-2 font-mono text-[11px] flex items-center gap-2 mt-3" style={{ background: `${C.green}14`, color: C.green, border: `1px solid ${C.green}33` }}>
        <CheckCircle2 className="h-3.5 w-3.5" /> Threat contained · action written to audit trail
      </motion.div>
    </div>
  );
}

function Compliance() {
  const rows = ["BACKUP_CREATED", "GXP_RECORD_SIGNED", "THREAT_RESOLVED", "AUTO_BACKUP"];
  return (
    <div className="p-6 relative">
      <Cursor delay={1.3} to={{ top: "15%", left: "76%", c: C.compliance }} />
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[10px] tracking-widest text-slate-400">AUDIT TRAIL</span>
        <Btn color={C.compliance}><ShieldCheck className="h-3 w-3" /> Verify chain</Btn>
      </div>
      {rows.map((a, i) => (
        <motion.div key={a} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 * i }} className="flex items-center justify-between border-b border-slate-200 py-1.5 font-mono text-[11px]">
          <span style={{ color: C.overview }}>{a}</span>
          <span className="text-slate-400">sha256:{(4531 + i * 917).toString(16)}a…</span>
        </motion.div>
      ))}
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 2.6 }} className="rounded-md px-3 py-2 font-mono text-[11px] flex items-center gap-2 mt-3" style={{ background: `${C.green}14`, color: C.green, border: `1px solid ${C.green}33` }}>
        <ShieldCheck className="h-3.5 w-3.5" /> AUDIT CHAIN INTACT · NO TAMPERING DETECTED
      </motion.div>
    </div>
  );
}

function AI() {
  const lines = ["Analyzing 6 workloads…", "Prioritizing 3 threats…", "Drafting 21 CFR Part 11 report…"];
  return (
    <div className="p-6 relative">
      <Cursor delay={1.1} to={{ top: "15%", left: "78%", c: C.ai }} />
      <div className="flex items-center justify-between mb-3">
        <span className="flex items-center gap-2 font-mono text-[11px] font-semibold" style={{ color: C.ai }}><Cpu className="h-4 w-4" /> VAULTVEC AI</span>
        <Btn color={C.ai}>Generate report</Btn>
      </div>
      {lines.map((l, i) => (
        <motion.div key={l} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 + i * 0.5 }} className="font-mono text-xs text-slate-500">↳ {l}</motion.div>
      ))}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 3.3 }} className="rounded-md border border-slate-200 bg-white p-3 mt-3 font-mono text-[11px] flex items-center justify-between">
        <span className="flex items-center gap-2" style={{ color: C.green }}><CheckCircle2 className="h-3.5 w-3.5" /> report.pdf ready · signed & hashed</span>
        <Btn color={C.green} filled><Download className="h-3 w-3" /> PDF</Btn>
      </motion.div>
    </div>
  );
}

function Done() {
  const items = [["Backed up", C.backup], ["Threats resolved", C.threat], ["Chain verified", C.compliance], ["Report exported", C.ai]];
  return (
    <div className="h-full flex flex-col items-center justify-center p-6 text-center">
      <div className="h-12 w-12 rounded-full flex items-center justify-center" style={{ background: `${C.green}1A` }}>
        <ShieldCheck className="h-7 w-7" style={{ color: C.green }} />
      </div>
      <h3 className="font-heading text-xl font-bold tracking-tight mt-4 text-slate-900">Protected, end to end.</h3>
      <div className="grid grid-cols-2 gap-2 mt-5">
        {items.map(([t, c], i) => (
          <motion.div key={t} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 * i }} className="flex items-center gap-2 font-mono text-[11px] text-slate-600">
            <CheckCircle2 className="h-3.5 w-3.5" style={{ color: c }} /> {t}
          </motion.div>
        ))}
      </div>
    </div>
  );
}

const SCENES = [
  { id: "login", nav: null, accent: C.overview, cap: "1 · A team member signs in to the secure console", dur: 3800, Panel: Login },
  { id: "overview", nav: "overview", accent: C.overview, cap: "2 · The Overview shows live protection posture at a glance", dur: 4600, Panel: Overview },
  { id: "backup", nav: "backup", accent: C.backup, cap: "3 · Run an on-demand backup — a verified restore point is sealed", dur: 5200, Panel: Backup },
  { id: "threat", nav: "threat", accent: C.threat, cap: "4 · A ransomware threat is detected and resolved in one click", dur: 5200, Panel: Threat },
  { id: "compliance", nav: "compliance", accent: C.compliance, cap: "5 · Verify the 21 CFR Part 11 audit chain is tamper-free", dur: 5200, Panel: Compliance },
  { id: "ai", nav: "ai", accent: C.ai, cap: "6 · Vaultvec AI drafts a signed, inspection-ready report", dur: 5600, Panel: AI },
  { id: "done", nav: null, accent: C.green, cap: "Done · Protected, monitored and audit-ready — end to end", dur: 4200, Panel: Done },
];

export function Explainer() {
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timer = useRef(null);

  useEffect(() => {
    if (!playing) return;
    timer.current = setTimeout(() => setActive((a) => (a + 1) % SCENES.length), SCENES[active].dur);
    return () => clearTimeout(timer.current);
  }, [active, playing]);

  const scene = SCENES[active];
  const Panel = scene.Panel;
  const showChrome = !!scene.nav;

  return (
    <div className="border border-slate-200 bg-white rounded-lg overflow-hidden shadow-xl">
      {/* browser chrome */}
      <div className="border-b border-slate-200 bg-slate-100 px-4 py-2.5 flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-[#F43F5E]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#F59E0B]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#16A34A]" />
        <span className="font-mono text-[10px] text-slate-400 ml-3 tracking-widest">vaultvec://console</span>
        <button
          onClick={() => setPlaying((p) => !p)}
          data-testid="explainer-playpause"
          className="ml-auto h-7 w-7 rounded border border-slate-300 flex items-center justify-center text-slate-500 hover:text-slate-900 hover:border-slate-500 transition-colors bg-white"
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
        </button>
      </div>

      {/* app body */}
      <div className="flex min-h-[360px] bg-slate-50">
        {showChrome && (
          <div className="w-48 shrink-0 border-r border-slate-200 bg-white py-3 hidden sm:block">
            {NAV.map((n) => {
              const isActive = n.key === scene.nav;
              return (
                <div key={n.key} className="relative">
                  {isActive && (
                    <motion.div layoutId="nav-hl" className="absolute inset-0 border-l-[3px]" style={{ borderColor: scene.accent, background: `${scene.accent}12` }} transition={{ type: "spring", stiffness: 400, damping: 32 }} />
                  )}
                  <div className="relative flex items-center gap-2.5 px-4 py-2.5 text-xs transition-colors" style={{ color: isActive ? scene.accent : "#64748B", fontWeight: isActive ? 600 : 400 }}>
                    <n.icon className="h-4 w-4" /> {n.label}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex-1 relative overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div key={scene.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35 }} className="h-full">
              <Panel />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* caption + timeline */}
      <div className="border-t border-slate-200 bg-white">
        <div className="px-4 py-3">
          <AnimatePresence mode="wait">
            <motion.p key={scene.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="font-mono text-[11px] tracking-wide text-slate-700">
              {scene.cap}
            </motion.p>
          </AnimatePresence>
        </div>
        <div className="flex gap-1 px-4 pb-4">
          {SCENES.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setActive(i)}
              data-testid={`explainer-scene-${s.id}`}
              className="flex-1 h-1.5 rounded-full bg-slate-200 relative overflow-hidden"
              aria-label={`Go to step ${i + 1}`}
            >
              {i < active && <div className="absolute inset-0 rounded-full" style={{ background: `${s.accent}80` }} />}
              {i === active && playing && (
                <motion.div key={active} className="absolute inset-0 rounded-full" style={{ background: scene.accent }} initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: scene.dur / 1000, ease: "linear" }} />
              )}
              {i === active && !playing && <div className="absolute inset-0 rounded-full" style={{ background: scene.accent }} />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
